import { type PropsWithChildren, useCallback, useEffect, useMemo } from 'react';

import { Button } from '@opendoor/atoms/button';
import { AuthSessionContextProvider, useAuth } from '@opendoor/auth-fe/context';
import { useImpersonationSnapshot } from '@opendoor/auth-fe/impersonation-state';
import { selectedCustomer } from '@opendoor/auth-fe/support-session';
import { useRouter } from 'next/router';

import { useObservability } from '../../helpers/observability';
import { COSMOS_URL } from '../globals';
import { createAuthClient } from './authClient';
import { fetchCustomerAction, impersonationSession } from './impersonation';
import { recordRegistrationSmsConsent } from './registrationSmsConsent';
import { createTrackAuthError } from './sessionErrorRouting';
import SessionRateLimitedPage from './SessionRateLimitedPage';
import {
  exitSupportSession,
  hasLegacyCustomerToken,
  isSupportApplicationUrl,
  isSupportRouteUnavailable,
  supportReauthenticationUrl,
  supportSignInUrl,
  urlWithoutSupportSelection,
} from './supportImpersonation';

const SessionLoading = () => (
  <main className="p-8" role="status">
    Loading customer session…
  </main>
);

const SupportSessionError = ({
  description,
  retry,
  signInAgain,
  title,
}: {
  description: string;
  retry?: () => void;
  signInAgain?: () => void;
  title: string;
}) => (
  <main className="flex max-w-160 flex-col gap-4 p-8" role="alert">
    <h1>{title}</h1>
    <p>{description}</p>
    <div className="flex gap-3">
      {retry ? (
        <Button
          aria-label="Try customer support sign-in again"
          analyticsName="cosmos-support-session-retry"
          onClick={retry}
        >
          Try again
        </Button>
      ) : null}
      {signInAgain ? (
        <Button
          aria-label="Sign in to customer support again"
          analyticsName="cosmos-support-session-sign-in"
          variant="secondary"
          onClick={signInAgain}
        >
          Sign in again
        </Button>
      ) : null}
      <Button
        aria-label="Exit customer support"
        analyticsName="cosmos-support-session-exit"
        variant="secondary"
        onClick={exitSupportSession}
      >
        Exit customer support
      </Button>
    </div>
  </main>
);

const SessionFailure = ({
  retry,
  signInAgain,
}: {
  retry: () => void;
  signInAgain?: () => void;
}) => (
  <SupportSessionError
    description="Sign in again or open a new customer link."
    retry={retry}
    signInAgain={signInAgain}
    title="Customer support session unavailable"
  />
);

const SupportSessionBoundary = ({
  children,
  customerUUID,
  currentHref,
}: PropsWithChildren<{ customerUUID: string | null; currentHref: string }>) => {
  const { authentication, refresh } = useAuth();
  const reauthenticationUrl = customerUUID
    ? supportReauthenticationUrl({ currentUrl: currentHref, customerUUID })
    : null;
  const handleSignInAgain = useCallback(() => {
    if (reauthenticationUrl) window.location.assign(reauthenticationUrl);
  }, [reauthenticationUrl]);
  const signInAgain = reauthenticationUrl ? handleSignInAgain : undefined;
  const verified =
    customerUUID !== null &&
    authentication.state === 'authenticated' &&
    authentication.user.impersonated &&
    authentication.user.customerUuid?.toLowerCase() === customerUUID;

  useEffect(() => {
    if (!verified || authentication.state !== 'authenticated') return;
    const { email, fullName, firstName } = authentication.user;
    impersonationSession.setImpersonatedCustomerIdentity({
      name: fullName || firstName || email || 'Customer',
      roleLabel: null,
    });
  }, [authentication, verified]);

  if (customerUUID !== null) {
    if (authentication.state === 'authenticating') return <SessionLoading />;
    if (authentication.state === 'error' && authentication.rateLimited) {
      return <SessionRateLimitedPage retry={refresh} />;
    }
    if (!verified) return <SessionFailure retry={refresh} signInAgain={signInAgain} />;
  }

  return children;
};

const SessionProvider = ({
  children,
  customerUUID,
  currentHref,
}: PropsWithChildren<{ customerUUID: string | null; currentHref: string }>) => {
  const { trackError } = useObservability();
  const trackAuthError = useMemo(() => createTrackAuthError(trackError), [trackError]);
  const client = useMemo(() => createAuthClient(trackAuthError), [trackAuthError]);

  return (
    <AuthSessionContextProvider
      key={customerUUID ?? 'customer-session'}
      authBaseUrl={COSMOS_URL}
      client={client}
      fetch={fetchCustomerAction}
      onLogout={customerUUID ? exitSupportSession : undefined}
      analyticsPrefix="cosmos"
      trackError={trackAuthError}
      recordMarketingSmsConsent={recordRegistrationSmsConsent}
    >
      <SupportSessionBoundary customerUUID={customerUUID} currentHref={currentHref}>
        {children}
      </SupportSessionBoundary>
    </AuthSessionContextProvider>
  );
};

const HardRedirect = ({ destination }: { destination: string | null }) => {
  useEffect(() => {
    if (destination) window.location.replace(destination);
  }, [destination]);
  return <SessionLoading />;
};

const SelectedSupportSession = ({
  children,
  currentHref,
  customerUUID,
  redirectUrl,
  supportOrigin,
}: PropsWithChildren<{
  currentHref: string;
  customerUUID: string;
  redirectUrl: string | null;
  supportOrigin: boolean;
}>) => {
  const { customerUUID: activeCustomerUUID } = useImpersonationSnapshot(impersonationSession);

  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
      return;
    }
    if (supportOrigin) impersonationSession.setImpersonatedCustomer(customerUUID);
  }, [customerUUID, redirectUrl, supportOrigin]);

  if (redirectUrl || !supportOrigin || activeCustomerUUID !== customerUUID) {
    return <SessionLoading />;
  }
  return (
    <SessionProvider customerUUID={customerUUID} currentHref={currentHref}>
      {children}
    </SessionProvider>
  );
};

const RestoredSupportSession = ({
  children,
  currentHref,
  customerUUID,
}: PropsWithChildren<{ currentHref: string; customerUUID: string }>) => {
  const router = useRouter();
  const destination = useMemo(() => {
    const url = new URL(currentHref);
    url.searchParams.set('customer', customerUUID);
    url.searchParams.delete('human_token');
    return `${url.pathname}${url.search}${url.hash}`;
  }, [currentHref, customerUUID]);

  useEffect(() => {
    void router.replace(destination, undefined, { shallow: true });
  }, [destination, router]);

  return (
    <SessionProvider customerUUID={customerUUID} currentHref={currentHref}>
      {children}
    </SessionProvider>
  );
};

const AuthProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const { customerUUID: activeCustomerUUID } = useImpersonationSnapshot(impersonationSession);
  const routeUrl = useMemo(() => new URL(router.asPath, 'https://cosmos.invalid'), [router.asPath]);
  const selection = selectedCustomer(routeUrl.search);
  const currentHref =
    typeof window === 'undefined'
      ? ''
      : new URL(`${routeUrl.pathname}${routeUrl.search}${routeUrl.hash}`, window.location.origin)
          .href;
  const supportOrigin = currentHref ? isSupportApplicationUrl(currentHref) : false;
  const cleanUrl = currentHref ? urlWithoutSupportSelection(currentHref) : null;

  if (supportOrigin && isSupportRouteUnavailable(routeUrl.pathname)) {
    return (
      <SupportSessionError
        description="Self-tour is not available during a customer support session."
        title="Page unavailable in customer support"
      />
    );
  }

  if (selection.error) {
    return (
      <SupportSessionError
        title="Invalid customer link"
        description="Open a new customer link or exit impersonation."
      />
    );
  }
  if (selection.customerUUID === null && hasLegacyCustomerToken(routeUrl.search)) {
    return <HardRedirect destination={cleanUrl} />;
  }

  if (selection.customerUUID !== null) {
    if (!currentHref) return <SessionLoading />;
    const redirectUrl = supportSignInUrl(currentHref);
    if (!supportOrigin && !redirectUrl) {
      return <HardRedirect destination={cleanUrl} />;
    }
    return (
      <SelectedSupportSession
        currentHref={currentHref}
        customerUUID={selection.customerUUID}
        redirectUrl={redirectUrl}
        supportOrigin={supportOrigin}
      >
        {children}
      </SelectedSupportSession>
    );
  }

  if (supportOrigin && activeCustomerUUID) {
    return (
      <RestoredSupportSession currentHref={currentHref} customerUUID={activeCustomerUUID}>
        {children}
      </RestoredSupportSession>
    );
  }

  return (
    <SessionProvider customerUUID={null} currentHref={currentHref}>
      {children}
    </SessionProvider>
  );
};

export default AuthProvider;
